var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useState } from 'react';
import { useEffectOnce, usePrevious, useUpdateEffect } from 'react-use';
import { searchApi } from 'apis/search';
import { Sentry, SentryTeam } from 'app/utils/sentry';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';
import { searchActions } from '../../search.actions';
import { convertFiltersStringToObject } from '../filters/convertFilters';
import { convertSortStringToObject } from '../sort/convertSort';
/** A hook that initializes the Search page. */
export function useSearchInit() {
    const dispatch = useAppDispatch();
    const { params: urlParams } = useAppSearchParams('/search/:tab');
    const { params } = useAppSelector((state) => state.search);
    const prevParams = usePrevious(params);
    const [isInitialized, setIsInitialized] = useState(false);
    const [readSearchEnrichedFiltersQuery] = searchApi.useLazyReadSearchEnrichedFiltersQuery();
    const updateSearchParams = useCallback((filters) => {
        const nextParams = {
            query: urlParams.q || '',
            filters,
            sort: convertSortStringToObject(urlParams.sort),
            strict: urlParams.strict === 'true' ? true : false,
            page: urlParams.page ? parseInt(urlParams.page) : 1,
        };
        dispatch(searchActions.params.update(nextParams));
    }, [dispatch, urlParams]);
    /**
     * Parses and enriches the filters from the URL params if necessary, then
     * updates the search Redux state with the initial params state.
     */
    const enrichAndUpdateParams = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        const parsedFilters = convertFiltersStringToObject(urlParams.filters);
        try {
            const shouldEnrich = parsedFilters.conditions.some((condition) => condition.values.some((value) => !value.label));
            if (shouldEnrich) {
                const { data: { filters: enrichedFilters }, } = yield readSearchEnrichedFiltersQuery({
                    filters: parsedFilters,
                }).unwrap();
                updateSearchParams(enrichedFilters);
            }
            else {
                updateSearchParams(parsedFilters);
            }
            // Even if the API request to enrich filters fails, we still fulfill with the partially parsed filters
            // It won't break the UI, but if any dynamic filters are set (e.g. agents or queues), their names won't be shown
        }
        catch (error) {
            updateSearchParams(parsedFilters);
        }
    }), [readSearchEnrichedFiltersQuery, updateSearchParams, urlParams]);
    const init = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        try {
            yield enrichAndUpdateParams();
        }
        catch (error) {
            Sentry.captureException(error, {
                extra: {
                    urlParams,
                },
                tags: {
                    team: SentryTeam.Insights,
                },
            });
        }
        finally {
            setIsInitialized(true);
        }
    }), [enrichAndUpdateParams, urlParams]);
    useEffectOnce(() => {
        init();
    });
    // Handles re-initializing the Search state when the URL params change through navigating with the
    // browser's back/forward buttons. The check for params state equality is necessary because the
    // URL params can also change through a regular state update, which should not trigger a re-init.
    useUpdateEffect(() => {
        if (Object.is(params, prevParams)) {
            init();
        }
    }, [urlParams]);
    return { isInitialized: isInitialized };
}
